<template>
  <div class="content-page">
    <TheBreadcrumb :pages="arianeRoute" />
    <HeaderContest :page="currentPageInfo" />
    <div class="post-container">
      <BrandArticle :page="article" />
    </div>
    <hr class="separator" />
    <section class="other section">
      <div class="title-line line">
        <h2 class="title-strat">Les autres actualités PlayStation</h2>
      </div>
      <div class="slider">
        <ActuPlaystationSlider :actus="actualities" />
      </div>
    </section>
    <DoNotMissSection
      v-if="doNotMissFiltered.length"
      :slider-items="doNotMissFiltered"
    />
    <p class="mention">
      * Récompense provisoire sous réserve de maintenir votre place au
      classement jusqu'à la fin du mois.
    </p>
  </div>
</template>

<script setup lang="ts">
import type { DoNotMiss } from '~/types'

definePageMeta({
  middleware: async (to) => {
    const loadGameContest = useLoadGameContest()
    return loadGameContest(to)
  },
})

const contestsStore = useContestsStore()
const { contest } = storeToRefs(contestsStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Tous les concours',
      route: '/concours',
    },
    {
      title: contest.value?.title,
      route: '',
    },
  ]
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title: `Concours ${contest.value?.title}`,
  script: [breadcrumbSchema],
})

useSeoMeta({
  title: `Concours ${contest.value?.title}`,
  ogTitle: `Concours ${contest.value?.title}`,
})

const actualitiesWAPStore = useActualitiesWAPStore()
const { actualities } = storeToRefs(actualitiesWAPStore)

const mediaQuery = useCustomMediaQuery()

const currentPageInfo = computed(() => {
  const image = getImage(contest, mediaQuery)

  const status = contest.value?.is_finished
    ? 'ended'
    : contest.value?.is_participating
      ? 'participated'
      : 'waiting'

  const withForm =
    contest.value?.type?.id === 2 || contest.value?.type?.id === 3
  const withAnswer = contest.value?.type?.id === 3

  return {
    title: contest.value?.title,
    titleImage: contest.value?.image_title?.url,
    image,
    prize: contest.value?.prize,
    status,
    date: contest.value?.displayed_ends_at
      ? new Date(contest.value?.displayed_ends_at)
      : undefined,
    withForm,
    withAnswer,
  }
})

const socialStore = useSocialStore()
const { social } = storeToRefs(socialStore)
const route = useRoute()

const article = computed(() => {
  const rawContest = unref(contest)
  return {
    date: rawContest?.displayed_starts_at,
    title: rawContest?.description ?? rawContest?.title,
    content1: rawContest?.content1,
    fullImage: rawContest?.image_content,
    content2: rawContest?.content2,
    cta1: {
      text: rawContest?.focus_title1,
      route: rawContest?.focus_url1
        ? rawContest?.focus_url1
        : rawContest?.focus_mail_to1,
      labelButton: rawContest?.focus_button_title1,
    },
    cta2: {
      text: rawContest?.focus_title2,
      route: rawContest?.focus_url2
        ? rawContest?.focus_url2
        : rawContest?.focus_mail_to2,
      labelButton: rawContest?.focus_button_title2,
    },
    sideContent: {
      card: rawContest?.article_linked?.slug
        ? {
            ...rawContest?.article_linked,
            route: `/actualites-playstation/${rawContest.article_linked.slug}`,
          }
        : undefined,
      community: rawContest?.community_linked?.slug
        ? {
            ...rawContest?.community_linked,
            route: `/communautes/${rawContest?.community_linked.slug}`,
          }
        : undefined,
    },
    comments: social.value.comments,
    commentsCount: social.value.comments_count,
    has_more_comments: social.value.has_more_comments,
    likes: social.value.likes_count,
    shares: social.value.shares_count,
    hide_comments: rawContest?.hide_comments,
    is_liked: social.value.is_liked,
    slug: route.params.contestName as string,
    type: 'contest', // OLD TODO
  }
})

const slidersStore = useDoNotMissStore()
const { doNotMiss } = storeToRefs(slidersStore)

const doNotMissFiltered = computed(() => {
  return doNotMiss.value?.filter(
    (item) =>
      !(
        item.slide_type === 'contest' && item.slug === route.params.contestName
      ),
  ) as DoNotMiss
})

const loadGameContest = useLoadGameContest()

const { authenticated } = useAuthenticated()

watch(authenticated, (newValue) => {
  if (newValue) {
    loadGameContest(route)
  }
})
</script>

<style lang="scss" scoped>
.content-page {
  .post-container {
    position: relative;

    .favicon {
      position: absolute;
      top: -4.4rem;
      left: 8rem;
      z-index: 2;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 8.4rem;
      height: 8.4rem;
      background: $white;
      border-radius: 4.2rem;

      @media screen and (max-width: 430px) {
        left: 2.5rem;
        width: 7rem;
        height: 7rem;
        border-radius: 3.5rem;
      }

      img {
        width: 5.6rem;
        height: 3.8rem;
        object-fit: contain;
      }
    }
  }

  hr {
    height: 1px;
    margin: 8rem 8rem 0;
    background-color: rgba($black, 0.2);
    border: 0;
  }

  .mention {
    margin-top: 10rem;
  }
}

@media screen and (max-width: 1024px) {
  .content-page {
    hr.separator {
      display: none;
    }
  }
}

@media screen and (max-width: 700px) {
  .content-page {
    .other {
      margin-top: 5rem;
      padding-right: 0;
      padding-left: 0;
    }

    .mention {
      margin: 2.5em;
    }
  }
}
</style>
